import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '../../components/Icon.tsx';
import { QrCode } from '../../components/Receipt.tsx';
import { Button, Confirm, EmptyState, Field, Skeleton, Switch } from '../../components/ui.tsx';
import { t } from '../../i18n/index.ts';
import { action } from '../../lib/api.ts';
import { money, parseMoney } from '../../lib/format.ts';
import type { Catalog } from '../../lib/types.ts';
import { useUi } from '../../lib/ui-state.tsx';
import { ManageLayout, useAction, useSave } from './common.tsx';

/**
 * Manage → Online: what guests can do from their phones at each location (look at the menu,
 * order from the table, order to pick up or to deliver), and the QR codes for the tables.
 */

interface Settings {
  id: string | null;
  location: string;
  menu: boolean;
  table_orders: boolean;
  pickup: boolean;
  delivery: boolean;
  delivery_product: string | null;
  delivery_min: number;
  delivery_area: string | null;
  prep_minutes: number;
  auto_send: boolean;
  paused_until: string | null;
  message: string | null;
}
interface Setup {
  tenant: string;
  locations: { id: string; name: string; code: string; settings: Settings }[];
  tables: { id: string; name: string; floor: string; floor_name: string; location: string; code: string }[];
}

const origin = () => window.location.origin;
export const tableUrl = (tenant: string, code: string) => `${origin()}/m/${tenant}/${code}`;
export const orderUrl = (tenant: string, locationCode: string) => `${origin()}/order/${tenant}/${locationCode.toLowerCase()}`;

export function Online() {
  const { data, reload } = useAction<Setup>('online.setup');
  const [tab, setTab] = useState<'ordering' | 'tables'>(() => (new URLSearchParams(window.location.search).get('tab') === 'tables' ? 'tables' : 'ordering'));
  const [locationId, setLocationId] = useState<string | null>(null);
  const loc = data?.locations.find((l) => l.id === locationId) ?? data?.locations[0] ?? null;

  return (
    <ManageLayout title={t('manage.online')} subtitle={t('onlineset.subtitle')}>
      <div className="segmented tabs" role="tablist">
        <button role="tab" aria-selected={tab === 'ordering'} className={tab === 'ordering' ? 'active' : ''} onClick={() => setTab('ordering')}>
          <Icon name="settings" size={18} /> {t('onlineset.tab_ordering')}
        </button>
        <button role="tab" aria-selected={tab === 'tables'} className={tab === 'tables' ? 'active' : ''} onClick={() => setTab('tables')}>
          <Icon name="qr" size={18} /> {t('onlineset.tab_tables')}
        </button>
      </div>
      {!data || !loc ? (
        <Skeleton h={320} r="var(--radius-lg)" />
      ) : (
        <>
          {data.locations.length > 1 ? (
            <div className="segmented subtle online-locs" role="radiogroup" aria-label={t('onlineset.location')}>
              {data.locations.map((l) => (
                <button key={l.id} role="radio" aria-checked={l.id === loc.id} className={l.id === loc.id ? 'active' : ''} onClick={() => setLocationId(l.id)}>
                  <Icon name="store" size={16} /> {l.name}
                </button>
              ))}
            </div>
          ) : null}
          {tab === 'ordering' ? (
            <Ordering key={loc.id} tenant={data.tenant} loc={loc} onSaved={() => void reload()} />
          ) : (
            <Tables tenant={data.tenant} tables={data.tables.filter((x) => x.location === loc.id)} enabled={loc.settings.menu} onChanged={() => void reload()} />
          )}
        </>
      )}
    </ManageLayout>
  );
}

function Ordering({ tenant, loc, onSaved }: { tenant: string; loc: Setup['locations'][number]; onSaved: () => void }) {
  const { busy, run } = useSave();
  const [s, setS] = useState<Settings>(loc.settings);
  const [minText, setMinText] = useState(loc.settings.delivery_min ? String(loc.settings.delivery_min / 100) : '');
  const { data: catalog } = useAction<Catalog>('products.catalog', { location: loc.id });
  const set = <K extends keyof Settings>(k: K, v: Settings[K]) => setS((x) => ({ ...x, [k]: v }));
  const items = useMemo(() => (catalog?.products ?? []).filter((p) => p.kind !== 'combo' && p.on_menu !== false).sort((a, b) => a.name.localeCompare(b.name)), [catalog]);
  const fee = items.find((p) => p.id === s.delivery_product);
  const min = minText.trim() ? parseMoney(minText) : 0;
  const errors = {
    product: s.delivery && !s.delivery_product ? t('onlineset.e_product') : null,
    min: min === null ? t('onlineset.e_min') : null,
    prep: !(s.prep_minutes >= 5 && s.prep_minutes <= 240) ? t('onlineset.e_prep') : null,
  };
  const invalid = Object.values(errors).some(Boolean);
  const web = s.pickup || s.delivery;
  const save = () =>
    void run(async () => {
      const { id: _id, paused_until: _p, ...rest } = s;
      await action('online.save_settings', { ...rest, location: loc.id, delivery_min: min ?? 0, delivery_area: s.delivery_area || null, message: s.message || null });
      onSaved();
    }, t('onlineset.saved'));

  return (
    <div className="online-settings">
      <section className="card">
        <header className="card-head">
          <div>
            <h2>
              <Icon name="qr" size={20} /> {t('onlineset.menu_title')}
            </h2>
            <span className="muted">{t('onlineset.menu_hint')}</span>
          </div>
          <Switch
            checked={s.menu}
            onChange={(v) => setS((x) => ({ ...x, menu: v, table_orders: v ? x.table_orders : false }))}
            label={t('onlineset.menu_on')}
          />
        </header>
        <div className="setting-row">
          <div>
            <strong>{t('onlineset.table_orders')}</strong>
            <p className="muted">{t('onlineset.table_orders_hint')}</p>
          </div>
          <Switch checked={s.table_orders} disabled={!s.menu} onChange={(v) => set('table_orders', v)} label={t('onlineset.table_orders')} />
        </div>
      </section>

      <section className="card">
        <header className="card-head">
          <div>
            <h2>
              <Icon name="bag" size={20} /> {t('onlineset.web_title')}
            </h2>
            <span className="muted">{t('onlineset.web_hint')}</span>
          </div>
        </header>
        <div className="setting-row">
          <div>
            <strong>{t('onlineset.pickup')}</strong>
            <p className="muted">{t('onlineset.pickup_hint')}</p>
          </div>
          <Switch checked={s.pickup} onChange={(v) => set('pickup', v)} label={t('onlineset.pickup')} />
        </div>
        <div className="setting-row">
          <div>
            <strong>{t('onlineset.delivery')}</strong>
            <p className="muted">{t('onlineset.delivery_hint')}</p>
          </div>
          <Switch checked={s.delivery} onChange={(v) => set('delivery', v)} label={t('onlineset.delivery')} />
        </div>
        {s.delivery ? (
          <div className="form-grid">
            <Field label={t('onlineset.fee_item')} hint={fee ? t('onlineset.fee_is', { price: money(fee.price) }) : t('onlineset.fee_item_hint')} error={errors.product}>
              <select className="input" value={s.delivery_product ?? ''} onChange={(e) => set('delivery_product', e.target.value || null)}>
                <option value="">{t('onlineset.choose_item')}</option>
                {items.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} · {money(p.price)}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={t('onlineset.min')} hint={t('onlineset.min_hint')} error={errors.min}>
              <input className="input" inputMode="decimal" value={minText} placeholder="0" onChange={(e) => setMinText(e.target.value)} />
            </Field>
            <Field label={t('onlineset.area')} hint={t('onlineset.area_hint')}>
              <input className="input" value={s.delivery_area ?? ''} maxLength={120} onChange={(e) => set('delivery_area', e.target.value)} />
            </Field>
          </div>
        ) : null}
        {web ? <ShareLink tenant={tenant} code={loc.code} /> : null}
      </section>

      <section className="card">
        <header className="card-head">
          <div>
            <h2>
              <Icon name="chef" size={20} /> {t('onlineset.kitchen_title')}
            </h2>
            <span className="muted">{t('onlineset.kitchen_hint')}</span>
          </div>
        </header>
        <div className="setting-row">
          <div>
            <strong>{t('onlineset.auto_send')}</strong>
            <p className="muted">{t('onlineset.auto_send_hint')}</p>
          </div>
          <Switch checked={s.auto_send} onChange={(v) => set('auto_send', v)} label={t('onlineset.auto_send')} />
        </div>
        <div className="form-grid">
          <Field label={t('onlineset.prep')} hint={t('onlineset.prep_hint')} error={errors.prep}>
            <input className="input" inputMode="numeric" value={s.prep_minutes || ''} onChange={(e) => set('prep_minutes', Number(e.target.value.replace(/\D/g, '')) || 0)} />
          </Field>
          <Field label={t('onlineset.message')} hint={t('onlineset.message_hint')}>
            <input className="input" value={s.message ?? ''} maxLength={160} onChange={(e) => set('message', e.target.value)} />
          </Field>
        </div>
        <p className="callout callout-info">
          <Icon name="shieldCheck" size={16} />
          <span>{t('onlineset.safety')}</span>
        </p>
      </section>

      <div className="form-actions">
        <Button variant="primary" icon="check" busy={busy} disabled={invalid} onClick={save}>
          {t('common.save')}
        </Button>
      </div>
    </div>
  );
}

/** The location's ordering page: its address to copy or open, and a QR code to print or share. */
function ShareLink({ tenant, code }: { tenant: string; code: string }) {
  const { toast } = useUi();
  const url = orderUrl(tenant, code);
  const [qr, setQr] = useState<string[] | null>(null);
  useEffect(() => {
    void action<string[][]>('online.qr', { texts: [url] }).then((r) => setQr(r[0] ?? null), () => setQr(null));
  }, [url]);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast(t('onlineset.copied'));
    } catch {
      window.prompt(t('onlineset.copy_by_hand'), url);
    }
  };
  return (
    <div className="online-share">
      {qr ? <QrCode rows={qr} label={t('onlineset.qr_for', { url })} /> : <Skeleton h={120} w={120} />}
      <div className="online-share-text">
        <strong>{t('onlineset.share_title')}</strong>
        <p className="muted">{t('onlineset.share_hint')}</p>
        <code className="online-url">{url}</code>
        <div className="online-share-actions">
          <Button size="sm" icon="copy" onClick={() => void copy()}>
            {t('onlineset.copy')}
          </Button>
          <a className="btn btn-ghost btn-sm" href={url} target="_blank" rel="noreferrer">
            <Icon name="link" size={16} />
            <span className="btn-label">{t('onlineset.open')}</span>
          </a>
        </div>
      </div>
    </div>
  );
}

function Tables({ tenant, tables, enabled, onChanged }: { tenant: string; tables: Setup['tables']; enabled: boolean; onChanged: () => void }) {
  const { fail, toast } = useUi();
  const [qrs, setQrs] = useState<Record<string, string[]>>({});
  const [renew, setRenew] = useState<Setup['tables'][number] | null>(null);
  const [printing, setPrinting] = useState(false);
  const [renewing, setRenewing] = useState(false);
  const key = tables.map((x) => x.code).join();
  useEffect(() => {
    if (!tables.length) return;
    void action<string[][]>('online.qr', { texts: tables.map((x) => tableUrl(tenant, x.code)) }).then(
      (rows) => setQrs(Object.fromEntries(tables.map((x, i) => [x.id, rows[i]!]))),
      fail,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, tenant]);
  useEffect(() => {
    if (!printing) return;
    const done = () => setPrinting(false);
    window.addEventListener('afterprint', done);
    const id = setTimeout(() => window.print(), 50);
    return () => {
      clearTimeout(id);
      window.removeEventListener('afterprint', done);
    };
  }, [printing]);
  const floors = [...new Set(tables.map((x) => x.floor_name))];

  if (!tables.length) return <EmptyState icon="table" title={t('onlineset.no_tables')} body={t('onlineset.no_tables_body')} />;
  return (
    <div className="online-tables">
      {!enabled ? (
        <p className="callout callout-warn">
          <Icon name="alert" size={16} /> <span>{t('onlineset.menu_off')}</span>
        </p>
      ) : null}
      <div className="online-tables-head">
        <p className="muted">{t('onlineset.tables_hint')}</p>
        <Button variant="primary" icon="printer" onClick={() => setPrinting(true)} disabled={Object.keys(qrs).length !== tables.length}>
          {t('onlineset.print_all')}
        </Button>
      </div>
      {floors.map((f) => (
        <section key={f} className="online-floor">
          <h3>{f}</h3>
          <div className="online-qr-grid">
            {tables
              .filter((x) => x.floor_name === f)
              .map((x) => (
                <article key={x.id} className="online-qr-card">
                  {qrs[x.id] ? <QrCode rows={qrs[x.id]!} label={t('onlineset.qr_table', { name: x.name })} /> : <Skeleton h={120} w={120} />}
                  <strong>{t('online.table', { name: x.name })}</strong>
                  <div className="online-qr-actions">
                    <a className="btn btn-ghost btn-sm" href={tableUrl(tenant, x.code)} target="_blank" rel="noreferrer" title={t('onlineset.open')}>
                      <Icon name="link" size={16} />
                      <span className="btn-label">{t('onlineset.open')}</span>
                    </a>
                    <Button size="sm" variant="ghost" icon="undo" onClick={() => setRenew(x)}>
                      {t('onlineset.new_code')}
                    </Button>
                  </div>
                </article>
              ))}
          </div>
        </section>
      ))}
      {renew ? (
        <Confirm
          title={t('onlineset.new_code_title', { name: renew.name })}
          body={t('onlineset.new_code_body')}
          confirm={t('onlineset.new_code')}
          danger
          busy={renewing}
          onClose={() => setRenew(null)}
          onConfirm={async () => {
            setRenewing(true);
            try {
              await action('online.new_table_code', { table: renew.id });
              toast(t('onlineset.new_code_done', { name: renew.name }));
              setRenew(null);
              onChanged();
            } catch (err) {
              fail(err);
            } finally {
              setRenewing(false);
            }
          }}
        />
      ) : null}
      {printing
        ? createPortal(
            <div className="print-sheet print-a4">
              <div className="qr-print-grid">
                {tables.map((x) =>
                  qrs[x.id] ? (
                    <div key={x.id} className="qr-print-card">
                      <QrCode rows={qrs[x.id]!} label={t('onlineset.qr_table', { name: x.name })} />
                      <strong>{t('online.table', { name: x.name })}</strong>
                      <span>{t('onlineset.print_scan')}</span>
                    </div>
                  ) : null,
                )}
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
